import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
    FormHelp,
    Input,
    Spacer,
} from '@zillow/constellation';

import { PHONE_VERIFICATION_STEPS } from 'app/shared/constants/WizardStepsConstants';
import numberUtils from 'app/shared/utils/numberUtils';
import StringConstants from 'app/shared/constants/StringConstants';
import PhoneVerificationBodyTemplate from '../PhoneVerificationBodyTemplate';

import { StyledWrapper } from './StyledComponents';
import { StyledCTALabel, StyledFullWidthButton } from '../../StyledComponents';

const propTypes = {
    currentStepIndex: PropTypes.number.isRequired,
    incrementStep: PropTypes.func.isRequired,
    onPhoneNumberAdded: PropTypes.func, // eslint-disable-line react/require-default-props
    savePhoneNumberToVerify: PropTypes.func.isRequired,
};

const AddPhoneNumber = ({
    currentStepIndex,
    incrementStep,
    onPhoneNumberAdded,
    savePhoneNumberToVerify,
}) => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [error, setError] = useState(null);
    const addPhoneNumberRef = useRef();

    useEffect(() => {
        addPhoneNumberRef.current.focus();
    }, []);

    const isPhoneNumberVerified = (phone) => {
        const cleanPhoneNumber = numberUtils.cleanPhoneNumber(phone);
        if (cleanPhoneNumber.length < 10) {
            setError(StringConstants.SS_ERRORS.invalid_phone_number);
            return;
        }

        if (onPhoneNumberAdded) {
            onPhoneNumberAdded(cleanPhoneNumber)
                .then(() => incrementStep(PHONE_VERIFICATION_STEPS.VERIFICATION_SUCCESS))
                .catch((err) => {
                    if (err === 'Verification required') {
                        savePhoneNumberToVerify(cleanPhoneNumber);
                        incrementStep();
                        return;
                    }
                    setError(err);
                });
        }
    };

    const onChangeHandler = (e) => {
        setError(null);
        setPhoneNumber(e.target.value);
    };

    const onKeyDownHandler = (e) => {
        if (e.key === 'Enter') {
            isPhoneNumberVerified(phoneNumber);
        }
    };

    const message = `To help protect your identity, Zillow will send a verification code to this number.
    You can choose whether to show the number on your listing.`;

    return (
        <PhoneVerificationBodyTemplate
            heading={`Step ${currentStepIndex + 1} - Add your phone number`}
            subheading={message}
        >
            <StyledWrapper>
                <StyledCTALabel>Your phone number:</StyledCTALabel>
                <Spacer marginBottom="xs">
                    <Input
                        error={error !== null}
                        onChange={onChangeHandler}
                        onKeyDown={onKeyDownHandler}
                        placeholder="(123) 456-7890"
                        ref={addPhoneNumberRef}
                        type="tel"
                        value={numberUtils.formatPhoneNumberInput(phoneNumber)}
                    />
                </Spacer>
                {error && <FormHelp error={true}>{error}</FormHelp>}
            </StyledWrapper>
            <StyledFullWidthButton
                buttonType="primary"
                onClick={() => isPhoneNumberVerified(phoneNumber)}
                size="md"
            >
                Next
            </StyledFullWidthButton>
        </PhoneVerificationBodyTemplate>
    );
};

AddPhoneNumber.propTypes = propTypes;

export default AddPhoneNumber;
